import Article from 'Api/Models/Article';

type Props = {
    article: Article;
    addSuggestion: CallableFunction;
};

const domain = window.location.origin;

export const getCountOfTags = (text: string, tag: string) => {
    return (text && text.split(tag).length - 1) || 0;
};

export const getCountOfInternalLinks = (text: string): number => {
    if (!text) {
        return 0;
    }

    // Escape dots in domain name for regex
    const escapedDomain = domain.replace(/\./g, '\\.');

    // Regex to match the domain in the text
    const regex = new RegExp(`\\b${escapedDomain}\\b`, 'gi');
    const matches = text.match(regex);

    return matches ? matches.length : 0;
};

export const getCountOfExternalLinks = (text: string): number => {
    if (!text) {
        return 0;
    }

    // Match words starting with "https://"
    const matches = text.match(/\bhttps:\/\/\S+/gi);
    return matches ? matches.length - getCountOfInternalLinks(text) : 0;
};

export function articleSeoChecker({ article, addSuggestion }: Props) {
    const metaDescriptionLength = article.seo_tag?.meta_description?.length || 0;
    if (metaDescriptionLength === 0 || metaDescriptionLength < 120 || metaDescriptionLength > 156) {
        addSuggestion(
            'seo_tag_meta_description',
            'length',
            `The meta description has invalid character length (under 120 characters). Up to 156 characters are available.
                Used characters: ${metaDescriptionLength}`,
            'bad'
        );
    } else {
        addSuggestion(
            'seo_tag_meta_description',
            'length',
            `Used characters: ${metaDescriptionLength}.
                Recommended limit: 156 characters`,
            'good'
        );
    }

    const metaTitleLength = article.seo_tag?.title?.length || 0;
    if (metaTitleLength < 50 || metaTitleLength > 60) {
        addSuggestion(
            'seo_tag_title',
            'length',
            `The meta title has invalid character length (under 50). Up to 60 characters are available to avoid truncating.
                Used characters: ${metaTitleLength}`,
            'bad'
        );
    } else {
        addSuggestion(
            'seo_tag_title',
            'length',
            `Used characters: ${metaTitleLength}.
                Recommended limit: 60 characters`,
            'good'
        );
    }

    const titleLength = article.title?.length || 0;
    if (titleLength < 50 || titleLength > 60) {
        addSuggestion(
            'title',
            'length',
            `The title has invalid character length (under 50). Up to 60 characters are available to avoid truncating.
                Used characters: ${titleLength}`,
            'bad'
        );
    } else {
        addSuggestion(
            'title',
            'length',
            `Used characters: ${titleLength}.
                Recommended limit: 60 characters`,
            'good'
        );
    }

    const ogDescriptionLength = article.seo_tag?.og_description?.length || 0;
    if (ogDescriptionLength < 50 || ogDescriptionLength > 160) {
        addSuggestion(
            'seo_tag_og_description',
            'length',
            `The OG description has invalid character length (under 50). Up to 160 characters are available to avoid truncating.
                Used characters: ${ogDescriptionLength}`,
            'bad'
        );
    } else {
        addSuggestion(
            'seo_tag_og_description',
            'length',
            `Used characters: ${ogDescriptionLength}.
                Recommended limit: 160 characters`,
            'good'
        );
    }

    const stripHtml = (html) => html.replace(/<[^>]*>/g, '');
    const textWordsCount = article.text ? stripHtml(article.text).trim().split(/\s+/).filter(Boolean).length : 0;
    if (textWordsCount < 300) {
        addSuggestion(
            'text',
            'words',
            `The text contains ${textWordsCount} word(s). The recommended minimum is 300 words`,
            'bad'
        );
    } else {
        addSuggestion('text', 'words', `The text contains ${textWordsCount} word(s).`, 'good');
    }

    const countOfH1Tags = getCountOfTags(article.text, '<h1>');
    addSuggestion('text', 'h1', `The text contains ${countOfH1Tags} h1 tag(s)`, countOfH1Tags === 1 ? 'good' : 'bad');
    addSuggestion('text', 'h2', `The text contains ${getCountOfTags(article.text, '<h2>')} h2 tag(s).`, 'info');
    addSuggestion('text', 'h3', `The text contains ${getCountOfTags(article.text, '<h3>')} h3 tag(s).`, 'info');
    addSuggestion('text', 'h4', `The text contains ${getCountOfTags(article.text, '<h4>')} h4 tag(s).`, 'info');
    addSuggestion('text', 'h5', `The text contains ${getCountOfTags(article.text, '<h5>')} h5 tag(s).`, 'info');
    addSuggestion('text', 'h6', `The text contains ${getCountOfTags(article.text, '<h6>')} h6 tag(s).`, 'info');

    const countOfImages = getCountOfTags(article.text, '<img');
    addSuggestion(
        'text',
        'img',
        `The text contains ${countOfImages} image(s).`,
        countOfImages > 0 ? 'good' : 'warning'
    );

    const countOfInternalLinks = getCountOfInternalLinks(article.text);
    addSuggestion(
        'text',
        'internal_links',
        `The text contains ${countOfInternalLinks} internal link(s).`,
        countOfInternalLinks > 0 ? 'good' : 'warning'
    );
    addSuggestion(
        'text',
        'external_links',
        `The text contains ${getCountOfExternalLinks(article.text)} external link(s).`,
        'info'
    );

    addSuggestion(
        'slug',
        'length',
        `The slug contains ${article.slug?.length || 0} character(s). Suggested range is 50-75 characters.`,
        'info'
    );
}
